/**
 * Third-party code must never compete with the first render. The document shell
 * installs this scheduler before React starts, so an early interaction is not
 * missed while the application bundle is still downloading.
 */
export const deferThirdPartyWork = (callback) => {
  if (typeof window === "undefined") return () => {};

  const schedule = window.__hotelzifyScheduleThirdParty;
  if (typeof schedule === "function") {
    return schedule(callback);
  }

  // The HTML shell normally provides the scheduler. This fallback keeps an
  // embedded build functional without reintroducing a timed startup task.
  const timer = window.setTimeout(callback, 0);
  return () => window.clearTimeout(timer);
};

/**
 * Force any queued third-party work to run right now, and make subsequent
 * scheduled work run immediately. Use on pages where conversions must fire
 * without waiting for interaction/idle (payment success, booking confirmation)
 * — these pages are not part of the PageSpeed budget.
 */
export const flushThirdPartyNow = () => {
  if (typeof window === "undefined") return;
  if (typeof window.__hotelzifyFlushThirdParty === "function") {
    window.__hotelzifyFlushThirdParty();
  }
};

/**
 * gtag.js is not designed to be loaded more than once per page: every
 * <script src="googletagmanager.com/gtag/js?id=..."> load re-initializes the
 * whole event-processing pipeline against the shared dataLayer, and a second
 * instance processing commands queued by the first throws internally
 * (undefined "groups"/"is_legacy_loaded") because the two instances' model
 * objects don't agree. Google's supported pattern is one script load, then
 * gtag("config", id) per additional id — so every call site in this app
 * (Gtag, GoogleHotelAds, the Ads bootstrap in index.html) must share a single
 * load through this helper instead of each injecting its own script tag.
 */
export const ensureGtagLoaded = (gtagId) => {
  if (typeof window === "undefined" || !gtagId) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () {
    window.dataLayer.push(arguments);
  };

  if (window.__hotelzifyGtagLoaded) return;
  window.__hotelzifyGtagLoaded = true;

  window.gtag("js", new Date());
  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`;
  script.async = true;
  document.head.appendChild(script);
};

/**
 * Configuring the same gtag id twice (e.g. a hotel's GA4 id also hardcoded
 * elsewhere) throws inside gtag.js/gtm.js the same way an undefined config
 * object does. window.__hotelzifyConfiguredGtagIds is shared with the
 * index.html bootstrap's own config calls (Ads ids configured before this
 * module ever loads), so every caller — this one included — dedupes against
 * one set instead of each tracking its own.
 */
export const configureGtagOnce = (gtagId, params) => {
  if (typeof window === "undefined" || !gtagId) return;

  window.__hotelzifyConfiguredGtagIds =
    window.__hotelzifyConfiguredGtagIds || new Set();
  if (window.__hotelzifyConfiguredGtagIds.has(gtagId)) return;
  window.__hotelzifyConfiguredGtagIds.add(gtagId);

  window.gtag("config", gtagId, params || {});
};

/**
 * Use for optional integrations which should never run in a non-interactive
 * session. Unlike deferThirdPartyWork this deliberately has no timed fallback.
 */
export const deferUntilUserIntent = (callback) => {
  if (typeof window === "undefined") return () => {};

  let hasRun = false;
  const events = ["pointerdown", "keydown", "touchstart"];
  const run = () => {
    if (hasRun) return;
    hasRun = true;
    events.forEach((eventName) => window.removeEventListener(eventName, run));
    callback();
  };

  events.forEach((eventName) =>
    window.addEventListener(eventName, run, { once: true, passive: true })
  );

  return () => {
    hasRun = true;
    events.forEach((eventName) => window.removeEventListener(eventName, run));
  };
};
